import { FileText, Loader2 } from 'lucide-react';

type PolicyHtmlContentProps = {
  html?: string;
  loading?: boolean;
  error?: string | null;
};

function getFriendlyPolicyMessage(error?: string | null) {
  const normalized = (error || '').trim().toLowerCase();
  if (
    !normalized ||
    normalized.includes('not found') ||
    normalized.includes('no content') ||
    normalized.includes('404')
  ) {
    return {
      title: 'Document not available yet',
      description:
        'This policy is being prepared and isn’t ready to view. Please check back soon, or go back and choose another document.',
    };
  }
  return {
    title: 'Unable to load this document',
    description:
      'Something went wrong while loading this policy. Please try again in a moment, or go back and open it from the list.',
  };
}

function PolicyEmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-20 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F2F6F9] text-[var(--dash-brand)]">
        <FileText className="h-7 w-7" strokeWidth={1.75} />
      </div>
      <h3 className="m-0 text-lg font-semibold tracking-tight text-[var(--dash-ink,#102649)]">
        {title}
      </h3>
      <p className="mt-2 mb-0 max-w-md text-sm leading-relaxed text-[var(--dash-muted)]">
        {description}
      </p>
    </div>
  );
}

export function PolicyHtmlContent({
  html,
  loading,
  error,
}: PolicyHtmlContentProps) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--dash-brand)]" />
        <p className="m-0 text-sm font-medium text-[var(--dash-muted)]">
          Loading content…
        </p>
      </div>
    );
  }

  if (error) {
    const message = getFriendlyPolicyMessage(error);
    return (
      <PolicyEmptyState
        title={message.title}
        description={message.description}
      />
    );
  }

  if (!html) {
    return (
      <PolicyEmptyState
        title="Document not available yet"
        description="This policy is being prepared and isn’t ready to view. Please check back soon, or go back and choose another document."
      />
    );
  }

  return (
    <>
      <div
        className="policy-content text-left text-[15px] leading-relaxed text-[#4A5565] md:text-base"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <style>{`
        .policy-content h1,
        .policy-content h2,
        .policy-content h3 {
          color: var(--dash-brand, #145E8C);
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.85rem;
          text-align: left;
        }
        .policy-content h1 { font-size: 1.75rem; }
        .policy-content h2 {
          font-size: 1.35rem;
          border-left: 4px solid #33A65B;
          padding-left: 0.75rem;
        }
        .policy-content h3 { font-size: 1.15rem; }
        .policy-content p {
          margin-bottom: 1.15rem;
          line-height: 1.8;
          text-align: left;
          color: #4A5565;
        }
        .policy-content ul,
        .policy-content ol {
          margin-bottom: 1.15rem;
          padding-left: 1.5rem;
          text-align: left;
        }
        .policy-content li {
          margin-bottom: 0.45rem;
          padding-left: 0.25rem;
        }
        .policy-content strong {
          color: var(--dash-ink, #102649);
          font-weight: 700;
        }
        .policy-content a {
          color: #007A78;
          text-decoration: underline;
          font-weight: 600;
        }
      `}</style>
    </>
  );
}
